/**
 * API client.
 *
 * Thin wrapper around `fetch` that:
 *   - Sends session cookies (`credentials: 'include'`)
 *   - Serializes JSON bodies
 *   - Surfaces the API's structured error envelope as an ApiError class
 *   - Returns parsed JSON for 2xx, null for 204
 *
 * Used by TanStack Query callbacks. Routes and contracts are typed against
 * @procurement/shared schemas to catch wire-mismatches at compile time.
 */

export interface ApiErrorEnvelope {
  statusCode: number;
  error: string;
  message: string;
  details?: { path: string; message: string }[];
}

export class ApiError extends Error {
  readonly statusCode: number;
  readonly errorCode: string;
  readonly details?: { path: string; message: string }[];

  constructor(envelope: ApiErrorEnvelope) {
    super(envelope.message);
    this.name = 'ApiError';
    this.statusCode = envelope.statusCode;
    this.errorCode = envelope.error;
    this.details = envelope.details;
  }

  isUnauthorized(): boolean {
    return this.statusCode === 401;
  }
  isForbidden(): boolean {
    return this.statusCode === 403;
  }
  isRowVersionConflict(): boolean {
    return this.errorCode === 'ROW_VERSION_CONFLICT';
  }
  isConflict(): boolean {
    return this.statusCode === 409;
  }
  isValidation(): boolean {
    return this.errorCode === 'VALIDATION_ERROR';
  }
}

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
}

// ---------------------------------------------------------------------------
// CSRF token storage
// ---------------------------------------------------------------------------
//
// Pass 25 adds a CSRF check on every state-changing API request. The token
// is HMAC of the session id; the server returns it from /auth/login and
// /auth/me. We keep it in module-local memory (not localStorage) so it
// can't be exfiltrated by a CSRF attacker — they'd need both the session
// cookie AND a foothold in the SPA's JS context to read it.
//
// On a full page refresh, the SPA's bootstrap calls /auth/me, which returns
// the token alongside the user, and `setCsrfToken` is called from the auth
// hook to restore it. Until then, mutations would fail closed with 403,
// which the SPA's auth boundary already handles by routing to /login.

let csrfToken: string | null = null;

/** Persist the CSRF token after login / me. Called from the auth hooks. */
export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

/** Read the CSRF token. Exposed mostly for diagnostics. */
export function getCsrfToken(): string | null {
  return csrfToken;
}

const MUTATING_METHODS = new Set<HttpMethod>(['POST', 'PATCH', 'PUT', 'DELETE']);

function buildUrl(path: string, query?: RequestOptions['query']): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    params.append(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

export async function apiRequest<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = 'GET', body, query, signal } = options;

  // Compose request headers:
  //   - Content-Type only when there's a JSON body
  //   - X-CSRF-Token on mutating requests when we have one to send. The
  //     server treats /api/auth/login and /api/auth/logout as CSRF-exempt;
  //     the header is harmless if sent on those.
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (MUTATING_METHODS.has(method) && csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  const init: RequestInit = {
    method,
    credentials: 'include',
    signal,
    headers: Object.keys(headers).length > 0 ? headers : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  };

  const response = await fetch(buildUrl(path, query), init);

  if (response.status === 204) {
    return null as T;
  }

  let payload: unknown;
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    payload = await response.json();
  } else {
    payload = await response.text();
  }

  if (!response.ok) {
    if (
      typeof payload === 'object' &&
      payload !== null &&
      'statusCode' in payload &&
      'error' in payload &&
      'message' in payload
    ) {
      throw new ApiError(payload as ApiErrorEnvelope);
    }
    throw new ApiError({
      statusCode: response.status,
      error: 'UNKNOWN',
      message: typeof payload === 'string' ? payload : 'Request failed',
    });
  }

  return payload as T;
}
